import React, { useEffect, useRef, useState, useCallback } from "react";
import { MessageCircle, X, Send, ShieldCheck, Truck, CreditCard, HelpCircle, Bot } from "lucide-react";
import api from "../lib/api";

const STORAGE_KEY = "vortex_chat_session_v1";

const TOPICS = [
    { id: "shipping", label: "Shipping Questions", icon: Truck },
    { id: "products", label: "Product Questions", icon: ShieldCheck },
    { id: "payment", label: "Payment Help", icon: CreditCard },
    { id: "support", label: "Contact Support", icon: HelpCircle },
];

const newSessionId = () => `sess-${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;

const readState = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return { sessionId: null, started: false, name: "", email: "", topic: null };
        return JSON.parse(raw);
    } catch { return { sessionId: null, started: false, name: "", email: "", topic: null }; }
};
const writeState = (s) => localStorage.setItem(STORAGE_KEY, JSON.stringify(s));

export const LiveChatWidget = () => {
    const [open, setOpen] = useState(false);
    const [state, setState] = useState(readState);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [hasUnread, setHasUnread] = useState(false);
    const scrollRef = useRef(null);

    const persistState = (next) => {
        setState(next);
        writeState(next);
    };

    // Poll thread state for messages while widget is open OR check unread badge in background
    const refresh = useCallback(async () => {
        if (!state.sessionId) return;
        try {
            const { data } = await api.get(`/chat/threads/${state.sessionId}`);
            if (data?.messages) {
                setMessages(data.messages);
                if (data.thread?.unread_customer && !open) setHasUnread(true);
            }
        } catch { /* ignore */ }
    }, [state.sessionId, open]);

    useEffect(() => {
        if (!state.sessionId) return;
        refresh();
        const id = setInterval(refresh, open ? 5_000 : 30_000);
        return () => clearInterval(id);
    }, [state.sessionId, open, refresh]);

    useEffect(() => {
        if (open && scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
        if (open) setHasUnread(false);
    }, [open, messages]);

    const startWithTopic = async (topic) => {
        // If we don't have name/email yet, still ask but pre-set the topic
        const sessionId = state.sessionId || newSessionId();
        persistState({ ...state, sessionId, topic, started: state.started });
    };

    const submitIdentity = async (e) => {
        e.preventDefault();
        if (!state.email.trim()) return;
        setBusy(true);
        try {
            const sessionId = state.sessionId || newSessionId();
            await api.post("/chat/threads", {
                session_id: sessionId,
                customer_name: state.name || null,
                customer_email: state.email,
                topic: state.topic || "support",
            });
            persistState({ ...state, sessionId, started: true });
            await refresh();
        } catch (err) {
            // Surface a generic error
        }
        setBusy(false);
    };

    const sendMessage = async (e) => {
        e?.preventDefault();
        if (!input.trim() || !state.sessionId || !state.started) return;
        setBusy(true);
        try {
            await api.post(`/chat/threads/${state.sessionId}/messages`, { content: input });
            setInput("");
            await refresh();
        } catch { /* ignore */ }
        setBusy(false);
    };

    const reset = () => {
        localStorage.removeItem(STORAGE_KEY);
        setState({ sessionId: null, started: false, name: "", email: "", topic: null });
        setMessages([]);
        setOpen(false);
    };

    return (
        <>
            {/* Floating button */}
            <button
                onClick={() => setOpen((v) => !v)}
                className={`fixed right-5 z-[80] w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all duration-500 active:scale-90 focus:outline-none focus:ring-4 focus:ring-[var(--gold)]/40 ${
                    open
                        ? "bg-black border border-[var(--gold)]/40 text-[var(--gold)]"
                        : "bg-gradient-to-br from-[var(--gold)] to-[#B8901A] text-black hover:scale-105"
                }`}
                style={{ bottom: "calc(1.25rem + var(--bottom-safe-offset, 0px))" }}
                aria-label={open ? "Close chat" : "Open chat"}
                data-testid="livechat-toggle"
            >
                {open ? <X size={22} /> : <MessageCircle size={22} />}
                {hasUnread && !open && (
                    <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-emerald-400 border-2 border-black animate-pulse" aria-label="New message" />
                )}
            </button>

            {/* Panel */}
            {open && (
                <div
                    className="fixed right-5 z-[80] w-[calc(100%-2.5rem)] max-w-sm h-[70vh] max-h-[560px] rounded-2xl flex flex-col overflow-hidden bg-[#0F1115] border border-[var(--gold)]/30 shadow-2xl transition-[bottom] duration-500"
                    style={{
                        animation: "slideUpFade 240ms ease-out",
                        bottom: "calc(6rem + var(--bottom-safe-offset, 0px))",
                    }}
                    data-testid="livechat-panel"
                >
                    <style>{`@keyframes slideUpFade { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }`}</style>

                    {/* Header */}
                    <div className="px-4 py-3 border-b border-white/10 bg-gradient-to-r from-[#15171D] to-[#0F1115] flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-[rgba(233,188,11,0.15)] flex items-center justify-center text-[var(--gold)]">
                            <Bot size={16} />
                        </div>
                        <div className="flex-1">
                            <div className="font-display font-bold text-white text-sm">Carbon Labs Support</div>
                            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-300">
                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 mr-1.5 animate-pulse" />
                                Async — we reply same-day
                            </div>
                        </div>
                        {state.started && (
                            <button
                                onClick={reset}
                                className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] hover:text-white"
                                data-testid="livechat-reset"
                            >
                                New
                            </button>
                        )}
                    </div>

                    {/* Body */}
                    {!state.started ? (
                        <div className="flex-1 overflow-y-auto p-4 space-y-4">
                            {!state.topic ? (
                                <>
                                    <p className="text-sm text-[var(--text-secondary)]">How can we help today?</p>
                                    <div className="grid grid-cols-2 gap-2">
                                        {TOPICS.map((t) => {
                                            const I = t.icon;
                                            return (
                                                <button
                                                    key={t.id}
                                                    onClick={() => startWithTopic(t.id)}
                                                    className="text-left px-3 py-3 rounded-xl border border-white/10 hover:border-[var(--gold)]/40 hover:bg-[rgba(233,188,11,0.05)] transition-colors group"
                                                    data-testid={`livechat-topic-${t.id}`}
                                                >
                                                    <I size={16} className="text-[var(--gold)] mb-1.5 group-hover:scale-110 transition-transform" />
                                                    <div className="text-xs font-mono uppercase tracking-wider text-white">{t.label}</div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </>
                            ) : (
                                <form onSubmit={submitIdentity} className="space-y-3" data-testid="livechat-identity-form">
                                    <p className="text-xs text-[var(--text-secondary)]">
                                        Topic: <strong className="text-[var(--gold)]">{TOPICS.find((t) => t.id === state.topic)?.label}</strong>{" "}
                                        <button type="button" onClick={() => persistState({ ...state, topic: null })} className="underline text-[var(--text-muted)] text-[10px] ml-1">change</button>
                                    </p>
                                    <input
                                        type="text"
                                        placeholder="Your name (optional)"
                                        value={state.name}
                                        onChange={(e) => persistState({ ...state, name: e.target.value })}
                                        className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                                        data-testid="livechat-name"
                                    />
                                    <input
                                        type="email"
                                        required
                                        placeholder="your@email.com"
                                        value={state.email}
                                        onChange={(e) => persistState({ ...state, email: e.target.value })}
                                        className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                                        data-testid="livechat-email"
                                    />
                                    <button type="submit" disabled={busy} className="btn-solid w-full" data-testid="livechat-start">
                                        {busy ? "Starting…" : "Start chat"}
                                    </button>
                                    <p className="text-[10px] text-[var(--text-muted)] text-center">We&apos;ll email you when we reply.</p>
                                </form>
                            )}
                        </div>
                    ) : (
                        <>
                            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="livechat-messages">
                                {messages.length === 0 && (
                                    <div className="text-center text-xs text-[var(--text-muted)] py-8">
                                        Say hi 👋 — describe what you need and we&apos;ll reply by email + here.
                                    </div>
                                )}
                                {messages.map((m) => (
                                    <div
                                        key={m.id}
                                        className={`flex ${m.sender === "customer" ? "justify-end" : "justify-start"}`}
                                        data-testid={`livechat-message-${m.sender}`}
                                    >
                                        <div
                                            className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${
                                                m.sender === "customer"
                                                    ? "bg-[var(--gold)] text-black rounded-br-sm"
                                                    : "bg-white/10 text-white rounded-bl-sm"
                                            }`}
                                        >
                                            {m.content}
                                            <div className={`mt-0.5 text-[9px] font-mono uppercase tracking-wider ${m.sender === "customer" ? "text-black/60" : "text-white/40"}`}>
                                                {new Date(m.created_at).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" })}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <form onSubmit={sendMessage} className="p-3 border-t border-white/10 flex gap-2">
                                <input
                                    type="text"
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    placeholder="Type a message…"
                                    className="flex-1 px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
                                    data-testid="livechat-input"
                                />
                                <button
                                    type="submit"
                                    disabled={busy || !input.trim()}
                                    className="w-10 h-10 shrink-0 rounded-full bg-[var(--gold)] text-black flex items-center justify-center disabled:opacity-40 hover:bg-[#F5C932]"
                                    aria-label="Send"
                                    data-testid="livechat-send"
                                >
                                    <Send size={14} />
                                </button>
                            </form>
                        </>
                    )}
                </div>
            )}
        </>
    );
};
